import * as React from 'react';

import {getReactButton} from "../../index";
import {RefObject} from "react";
import {ReactButtonState} from "./ReactButton";

interface ReactCustomObjectData {
    id: string,
    index: number,
    row: any,
    displayOverride?: (rowObj: any, disabled: boolean, index: number) => any,
    appendJQueryObj?: (rowObj: any, disabled: boolean, index: number) => any,
    refreshID?: string,
    onRefresh?: () => {},
    disabled?: boolean,
    disableID?: string,
    css?: string,
    style?: {},
    JQueryObjectCSS?: string,
    JQueryObjectStyle?: {},
    displayObjectCSS?: string,
    displayObjectStyle?: {},
    rowIndex?: number,
    onClick?: (rowObj: any, rowIndex: number) => void,
    stopPropagationOnClick?: boolean,
    blockTextEntryClicks?: boolean,  // If true, prevent onClick from being called when clicking on textareas/selects/text inputs
    onJQueryObjectReady?: () => void,  // Called after jQuery objects are appended to the DOM
    onKeyDown?: (rowOBJ: any, rowIndex: number, keyPressed: string, bShift: boolean, bCTRL: boolean, bALT: boolean) => null,
    onKeyUp?: (rowOBJ: any, rowIndex: number, keyPressed: string, bShift: boolean, bCTRL: boolean, bALT: boolean) => null,
    stopPropagationOnKeyPress?: (rowOBJ: any, rowIndex: number, keyPressed: string, bOnKeyUp: boolean, bShift: boolean, bCTRL: boolean, bALT: boolean) => boolean,
}

interface ReactCustomObjectState {
    row: any,
    disabled: boolean
}

export class ReactCustomObject extends React.Component<ReactCustomObjectData> {
    customJQueryObjRef: RefObject<any>;
    constructor(props: ReactCustomObjectData) {
        super(props);

        this.customJQueryObjRef = React.createRef();

        this.state = {
            row: this.props.row,
            disabled: this.props.disabled
        };

        this.onRefresh = this.onRefresh.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.onDisableClick = this.onDisableClick.bind(this);
        this.onKeyPress = this.onKeyPress.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
    }

    componentDidMount() {
        this.buildJQueryObject();
    }

    // Update the state externally
    componentWillReceiveProps(props: ReactCustomObjectData) {
        this.setState({
            rows: props.row,
            disabled: props.disabled,
        }, () => {this.buildJQueryObject();});
    }

    onRefresh() {
        if(null != this.props.onRefresh) {
            this.props.onRefresh();
        }

        this.setState(() => ({
            row: (this.state as ReactCustomObjectState).row,
        }));
    }

    onKeyPress(event: any) {
        if(null != this.props.onKeyDown) {
            this.props.onKeyDown((this.state as ReactCustomObjectState).row, this.props.rowIndex, event.key, event.shiftKey, event.ctrlKey, event.altKey);
        }
        
        if(null != this.props.stopPropagationOnKeyPress) {
            const selectIndex = this.props.rowIndex;
            const selectRow = (this.state as ReactCustomObjectState).row;
            if(this.props.stopPropagationOnKeyPress(selectRow, selectIndex, event.key, false, event.shiftKey, event.ctrlKey, event.altKey)) {
                event.stopPropagation();
            }
        }
    }

    onKeyUp(event: any) {
        if(null != this.props.onKeyUp) {
            this.props.onKeyUp((this.state as ReactCustomObjectState).row, this.props.rowIndex, event.key, event.shiftKey, event.ctrlKey, event.altKey);
        }

        if(null != this.props.stopPropagationOnKeyPress) {
            const selectIndex = this.props.rowIndex;
            const selectRow = (this.state as ReactCustomObjectState).row;
            if(this.props.stopPropagationOnKeyPress(selectRow, selectIndex, event.key, true, event.shiftKey, event.ctrlKey, event.altKey)) {
                event.stopPropagation();
            }
        }
    }

    handleClick(event: any) {
        // If blockTextEntryClicks is enabled, check if the click target is a text entry element
        if (this.props.blockTextEntryClicks) {
            const target = event.target as HTMLElement;
            if (target) {
                const tagName = target.tagName;
                if (tagName === 'TEXTAREA' || tagName === 'SELECT') {
                    event.stopPropagation();
                    return;
                }
                if (tagName === 'INPUT') {
                    const input = target as HTMLInputElement;
                    const inputType = (input.type || '').toLowerCase();
                    if (inputType !== 'radio' && inputType !== 'checkbox') {
                        event.stopPropagation();
                        return;
                    }
                }
            }
        }

        if(null != this.props.onClick) {
            this.props.onClick((this.state as ReactCustomObjectState).row, this.props.rowIndex);
        }

        if(null != this.props.stopPropagationOnClick && this.props.stopPropagationOnClick) {
            event.stopPropagation();
        }
    }

    onDisableClick(event: any) {
        const isChecked = $(event.currentTarget).is(":checked");
        this.setState({
            disabled: isChecked
        });
    }

    buildJQueryObject() {
        const container = this.customJQueryObjRef.current as HTMLElement;
        if (null == container) {
            return;
        }

        // FIREFOX FIX: Do not tear down and recreate the jQuery-managed
        // subtree while the user is interacting with something inside it
        // (e.g. a native <select> dropdown is open, or a text field has
        // focus). Parent components re-render very frequently during
        // typing/filtering which would otherwise empty and re-append the
        // jQuery DOM nodes -- Firefox closes its native <select> popup the
        // instant the backing element is removed from the document.
        const active = (typeof document !== 'undefined') ? document.activeElement as HTMLElement : null;
        const activeInside = !!(active && active !== document.body && container.contains(active));
        if (activeInside) {
            return;
        }

        // Optimization: skip DOM churn if the regenerated HTML is identical.
        if (null != this.props.appendJQueryObj) {
            const fresh = this.props.appendJQueryObj(
                (this.state as ReactCustomObjectState).row,
                (this.state as ReactCustomObjectState).disabled,
                this.props.index
            );

            const $fresh = $('<div>').append(fresh);
            const newHtml = $fresh.html();
            const currentHtml = $(container).html();

            if (currentHtml && newHtml === currentHtml) {
                return;
            }

            $(container).empty();
            $(container).append($fresh.contents());
        } else {
            $(container).empty();
        }

        // Notify parent that jQuery objects are now in the DOM
        if (this.props.onJQueryObjectReady) {
            this.props.onJQueryObjectReady();
        }
    }

    buildDisplayObject(row: any, disabled: boolean) {
        return (null != this.props.displayOverride) ? this.props.displayOverride(row, disabled, this.props.index) : null;
    }

    render() {
        return <div id={this.props.id} className={this.props.css} style={this.props.style} onClick={this.handleClick} onKeyDown={(event) => this.onKeyPress(event)} onKeyUp={(event) => this.onKeyPress(event)}>
            {<table className="categoryAllHide"><tr>
                <td><input
                    id={this.props.disableID}
                    type="checkbox"
                    onChange={this.onDisableClick}
                    checked={(this.state as ReactButtonState).disabled}
                /></td>
                <td>
                    HIDDEN DISABLE BUTTON
                </td>
            </tr></table>
            }
            {getReactButton({id: this.props.refreshID, css: "categoryAllHide", onClick: this.onRefresh, text: "HIDDEN REFRESH BUTTON"})}
                <div ref={this.customJQueryObjRef} className={this.props.JQueryObjectCSS} style={this.props.JQueryObjectStyle}></div>
                <div className={this.props.displayObjectCSS} style={this.props.displayObjectStyle}>
                    {this.buildDisplayObject((this.state as ReactCustomObjectState).row, (this.state as ReactCustomObjectState).disabled)}
                </div>
            </div>;
    }
}
